import {getCookie, SYSTEM_COOKIES} from "./cookies.ts";
import {newAlertEvent} from "./events.ts";
import i18n from './i18n.ts';
import Sentry from "./sentry.ts";
import {getLocalStorageItem, SYSTEM_LOCAL_STORAGE} from "./localStorage.ts";

type PostOptions = {
  body?: unknown;
  headers?: HeadersInit;
  formData?: FormData;
}

function buildHeaders(options?: PostOptions): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${getCookie(SYSTEM_COOKIES.TOKEN)}`,
  };

  // Add organization header if available
  const orgId = getLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID);
  if (orgId) {
    headers['X-Organization-Id'] = orgId;
  }

  // Add Content-Type for JSON body
  if (options?.body && !options?.formData) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

function isJSONErrorResponse(response: unknown): response is { error: string } {
  return (response as { error: string }).error !== undefined;
}

function isJSONValidationErrorResponse(response: unknown): response is { errors: Record<string, string[]> } {
  return (response as { errors: Record<string, string[]> }).errors !== undefined;
}

function formatValidationErrors(errors: Record<string, string[]>): string {
  // Convert { email: ["is invalid"], password: ["is too short"] }
  // to "Email is invalid. Password is too short."
  return Object.entries(errors)
    .map(([field, messages]) => {
      const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
      return messages.map(msg => `${fieldName} ${msg}`).join('. ');
    })
    .join('. ');
}

function handleFetchError(err: Error) {
  if(import.meta.env.DEV) {
    console.error(err);
  } else {
    Sentry.captureException(err);
  }
  // Try to translate the error, but if no translation exists, use the message as-is
  // (validation errors from the API are already human-readable)
  const translationKey = `errors.${err.message}`;
  const translated = i18n.t(translationKey);
  const message = translated === translationKey ? err.message : translated;
  newAlertEvent({message, type: 'error'});
}

async function fetchResponseHandler<T>(res: Response): Promise<T> {
  if(!res.ok) {
    try {
      const jsonResponse = await res.json();
      if (isJSONErrorResponse(jsonResponse)) {
        throw new Error(jsonResponse.error);
      } else if (isJSONValidationErrorResponse(jsonResponse)) {
        throw new Error(formatValidationErrors(jsonResponse.errors));
      } else {
        throw new Error(!res.statusText || res.statusText === '' ? 'genericResponseError' : res.statusText);
      }
    } catch (e) {
      if (e instanceof Error && e.message && e.message !== 'genericResponseError') {
        throw e;
      }
      if (res.status === 401) {
        throw new Error('unauthorized');
      } else {
        throw new Error(!res.statusText || res.statusText === '' ? 'genericResponseError' : res.statusText);
      }
    }
  }
  if(res.status === 204) return {} as T;
  return res.json();
}

export async function patch<T>(url: URL, options: PostOptions): Promise<T> {
  return fetch(url.toString(), {
    method: "PATCH",
    body: options.formData ?? (options.body ? JSON.stringify(options.body) : null),
    headers: {
      ...buildHeaders(options),
      ...options.headers
    },
    credentials: 'include',
  })
    .then<T>(fetchResponseHandler)
    .catch(err => {
      handleFetchError(err);
      throw err;
    });
}

export async function put<T>(url: URL, options: PostOptions): Promise<T> {
  return fetch(url.toString(), {
    method: "PUT",
    body: options.formData ?? (options.body ? JSON.stringify(options.body) : null),
    headers: {
      ...buildHeaders(options),
      ...options.headers
    },
    credentials: 'include',
  })
    .then<T>(fetchResponseHandler)
    .catch(err => {
      handleFetchError(err);
      throw err;
    });
}

export async function post<T>(url: URL, options: PostOptions): Promise<T> {
  return fetch(url.toString(), {
    method: "POST",
    body: options.formData ?? (options.body ? JSON.stringify(options.body) : null),
    headers: {
      ...buildHeaders(options),
      ...options.headers
    },
    credentials: 'include',
  })
    .then<T>(fetchResponseHandler)
    .catch(err => {
      handleFetchError(err);
      throw err;
    });
}

export async function get<T>(url: URL): Promise<T> {
  return fetch(url.toString(), {
    method: "GET",
    headers: buildHeaders(),
    credentials: 'include',
  })
    .then<T>(fetchResponseHandler)
    .catch(err => {
      handleFetchError(err);
      throw err;
    });
}

export async function _delete<T>(url: URL): Promise<T> {
  return fetch(url.toString(), {
    method: "DELETE",
    headers: buildHeaders(),
    credentials: 'include',
  })
    .then<T>(fetchResponseHandler)
    .catch(err => {
      handleFetchError(err);
      throw err;
    });
}